import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button as _Button, Icon, Input, Label, Link, COLORS, spacing, Toggle } from '@livecommerce/design-system';
import { styled } from '@linaria/react';
import { useFirebase } from 'components/Firebase/index.js';
import { useNotifications } from 'hooks/index.js';
import { trackingService } from '../../services/tracking/TrackingService.js';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import { Loader } from '@bambuser-ui';
import { useUser } from 'contexts/UserContext.jsx';

const BUTTON_STATE = {
  ENABLED: 'enabled',
  DISABLED: 'disabled',
  LOADING: 'loading',
};

const Container = styled.div`
  padding-top: 24px;
  display: flex;
  flex-direction: column;
  gap: ${spacing(16)};
`;

const UploadLabel = styled.span`
  visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
`;

const Button = styled(_Button)`
  align-self: flex-start;
`;

const MicrosoftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing(4)};
`;

const MicrosoftContainer = styled.div`
  display: flex;
  gap: ${spacing(8)};
  align-items: center;
`;

const MicrosoftProfile = styled.div`
  display: flex;
  height: 48px;
  align-items: center;
  gap: ${spacing(8)};
  padding: ${spacing(16)};
  border: 1px solid ${COLORS.light_600};
  border-radius: 8px;
`;

const AccountAndSecurity = () => {
  const [buttonState, setButtonState] = useState(BUTTON_STATE.ENABLED);
  const authUser = useSelector((state) => state.sessionState.authUser);
  const email = authUser?.email;
  const firebase = useFirebase();
  const [providerData, setProviderData] = useState(firebase.getIdentityProviderData());
  const [loadingMicrosoftResponse, setLoadingMicrosoftResponse] = useState(false);
  const { userData, updateUserData } = useUser();
  const { optOutPlatformEmails } = userData;
  const [optOutPlatformEmailsLocalValue, setOptOutPlatformEmailsLocalValue] = useState(optOutPlatformEmails);

  useEffect(() => {
    setOptOutPlatformEmailsLocalValue(optOutPlatformEmails);
  }, [optOutPlatformEmails]);

  const { t, getErrorMessage } = useLocalization();
  const { handleFooterNotification } = useNotifications();

  const isLoading = buttonState === BUTTON_STATE.LOADING;

  const handleUpdatePasswordClick = async () => {
    if (buttonState !== BUTTON_STATE.ENABLED) return;

    setButtonState(BUTTON_STATE.LOADING);
    let actionSuccess = false;
    try {
      await firebase.doPasswordReset(email);
      setButtonState(BUTTON_STATE.DISABLED);
      handleFooterNotification({
        title: t('profile.changePasswordNotificationTitle'),
        message: t('profile.changePasswordNotificationDescription'),
      });
      actionSuccess = true;
    } catch (e) {
      setButtonState(BUTTON_STATE.ENABLED);
      handleFooterNotification({
        title: t('profile.changePasswordErrorTitle'),
        message: getErrorMessage(e.code, e.message),
        type: 'error',
      });
    } finally {
      trackingService.track({
        eventType: 'profile-modal:send-change-password-email',
        actionSuccess,
      });
    }
  };

  const microsoftProviderData = providerData.find((providerData) => providerData.providerId.includes('microsoft'));

  const onLinkMicrosoft = async () => {
    try {
      setLoadingMicrosoftResponse(true);
      await firebase.linkMicrosoftAccount();
      setProviderData(firebase.getIdentityProviderData());
      setLoadingMicrosoftResponse(false);
      handleFooterNotification({
        title: t('profile.linkSuccessText'),
        type: 'success',
      });
    } catch (e) {
      handleFooterNotification({
        title: t('profile.linkFailedErrorText'),
        message: e.message,
        type: 'error',
      });
    }
  };
  const unlinkMicrosoft = async () => {
    try {
      setLoadingMicrosoftResponse(true);
      await firebase.unlinkMicrosoftAccount();
      setProviderData(firebase.getIdentityProviderData());
      setLoadingMicrosoftResponse(false);
      handleFooterNotification({
        title: t('profile.unlinkSuccessText'),
        type: 'success',
      });
    } catch (e) {
      handleFooterNotification({
        title: t('profile.unlinkFailedErrorText'),
        message: e.message,
        type: 'error',
      });
    }
  };

  const handleOptOutNotificationsChange = async (event) => {
    const optOut = !event.target.checked;
    await updateUserData({ optOutPlatformEmails: optOut });
    setOptOutPlatformEmailsLocalValue(optOut);
  };

  return (
    <Container>
      <Input placeholder="jane.smith@company.com" label={t('profile.email')} value={email} disabled />
      <Toggle
        label={t('profile.receiveBamHubEmailNotifications')}
        labelVariation="bold"
        value={!optOutPlatformEmailsLocalValue}
        onChange={handleOptOutNotificationsChange}
      />
      <Button type="button" variant="regular" onClick={handleUpdatePasswordClick} disabled={isLoading}>
        <Loader loading={isLoading} type="small" />
        <UploadLabel visible={!isLoading}>
          {buttonState === BUTTON_STATE.ENABLED ? t('profile.changePasswordButtonLabel') : 'Email sent'}
        </UploadLabel>
      </Button>
      <MicrosoftSection>
        <Label>{t('profile.microsoftSectionLabel')}</Label>
        {microsoftProviderData ? (
          <MicrosoftContainer>
            <MicrosoftProfile>
              <Icon name="microsoft" />
              <Label>{microsoftProviderData.email}</Label>
            </MicrosoftProfile>
            {loadingMicrosoftResponse ? (
              <Loader loading={loadingMicrosoftResponse} type="small" position="relative" />
            ) : (
              <Link
                variant="subtle"
                theme="light"
                onClick={unlinkMicrosoft}
              >
                {t('profile.unlinkLabel')}
              </Link>
            )}
          </MicrosoftContainer>
        ) : (
          <Button
            variant="hollow"
            type="button"
            iconName="microsoft"
            disabled={loadingMicrosoftResponse}
            onClick={onLinkMicrosoft}
          >
            {t('profile.linkButtonLabel')}
          </Button>
        )}
      </MicrosoftSection>
    </Container>
  );
};

export default AccountAndSecurity;
